// Where to draw the "— ad break —" divider in a re-timed podcast transcript.
//
// A re-timed transcript is ElevenLabs-shaped plus `unaligned_ranges:
// [{start, end}]` — stretches of THIS listener's audio copy that carry no
// transcribed speech, i.e. dynamically inserted ads. No word covers them, so
// audio_sync's gap handling already clears the highlight while one plays. This
// module only answers the presentational question: which word boundaries should
// show a divider.
//
// Returned indexes mean "insert a divider after words[index]". Index -1 means
// "before the first word" — a pre-roll, the most common insertion slot.
//
// Deliberately dependency-free — no imports, one `export default` — so
// test/javascript/ad_breaks_test.js can load it with plain Node and no
// node_modules.

function toSeconds(value) {
  const number = parseFloat(value);
  return Number.isFinite(number) ? number : null;
}

// Half-open intersection: a range that starts exactly where the gap ends does
// not intersect it.
function intersects(range, gap) {
  return range.start < gap.end && range.end > gap.start;
}

function normalizeRanges(unalignedRanges) {
  if (!Array.isArray(unalignedRanges)) {
    return [];
  }

  return unalignedRanges
    .map(range => {
      if (!range || typeof range !== 'object') {
        return null;
      }

      const start = toSeconds(range.start);
      const end = toSeconds(range.end);

      if (start === null || end === null || end <= start) {
        return null;
      }

      return { start, end };
    })
    .filter(Boolean);
}

// Gaps of silence between consecutive words, plus the lead-in before the first
// word. A gap is where an insertion can hide; a range that overlaps one is an ad.
function silentGaps(words) {
  const gaps = [];
  const firstStart = toSeconds(words[0].start);

  if (firstStart !== null && firstStart > 0) {
    gaps.push({ index: -1, start: 0, end: firstStart });
  }

  for (let index = 0; index < words.length - 1; index += 1) {
    const end = toSeconds(words[index].end);
    const nextStart = toSeconds(words[index + 1].start);

    if (end === null || nextStart === null || nextStart <= end) {
      continue;
    }

    gaps.push({ index, start: end, end: nextStart });
  }

  return gaps;
}

function adBreakWordIndexes(words, unalignedRanges) {
  if (!Array.isArray(words) || words.length === 0) {
    return [];
  }

  const ranges = normalizeRanges(unalignedRanges);

  if (ranges.length === 0) {
    return [];
  }

  // One divider per gap, however many ranges land in it.
  return silentGaps(words)
    .filter(gap => ranges.some(range => intersects(range, gap)))
    .map(gap => gap.index);
}

export default adBreakWordIndexes;
